import type { GridInterface } from '@ad-lucy/context/adData';
import type { KnownBreakpoints } from '@ad-lucy/context/ThemeProvider';
import { LoggerService } from '@ad-lucy/utils';

const logger = LoggerService.getLogger().getModuleLogger('filterGridByBreakpointOption');

/**
 * Groups grid configurations by their breakpoints
 *
 * @param {GridInterface[]} grids - Array of grid interface objects to be grouped
 * @returns {Record<KnownBreakpoints, GridInterface[]>} Object with breakpoints as keys and arrays of matching grids as values
 */
const getGridByBreakpoints = (
  grids: GridInterface[],
): Record<KnownBreakpoints, GridInterface[]> => {
  return grids.reduce(
    (acc, grid) => ({
      ...acc,
      [grid.breakpoint as KnownBreakpoints]: [
        ...(acc[grid.breakpoint as KnownBreakpoints] ?? []),
        grid,
      ],
    }),
    {} as Record<KnownBreakpoints, GridInterface[]>,
  );
};

/**
 * Retrieves the appropriate group ID for a specific breakpoint
 *
 * @param {KnownBreakpoints} breakpoint - The breakpoint to get the group ID for
 * @param {GridInterface[]} grids - Array of grid interface objects
 * @param {Record<KnownBreakpoints, string | null | undefined>} optionIds - Record of breakpoints to option IDs
 * @returns {string | undefined} The group ID for the specified breakpoint or undefined if not found
 */
const getGroupId = (
  breakpoint: KnownBreakpoints,
  grids: GridInterface[],
  optionIds: Record<KnownBreakpoints, string | null | undefined>,
): string | undefined => {
  const id = optionIds[breakpoint];
  if (id) return id;

  // If no id is specified, the first groupId is used
  return grids.find((grid) => Boolean(grid.groupId))?.groupId;
};

/**
 * Filters grid configurations based on option IDs for different breakpoints
 *
 * @param {GridInterface[]} grids - Array of grid interface objects to be filtered
 * @param {Record<KnownBreakpoints, string | null | undefined>} optionIds - Record of breakpoints to option IDs
 * @returns {GridInterface[]} Filtered array of grid interface objects
 */
export const filterGridByBreakpointOption = (
  grids: GridInterface[],
  optionIds: Record<KnownBreakpoints, string | null | undefined>,
): GridInterface[] => {
  // Separate grids with and without breakpoints
  const gridsWithBreakpoint = grids.filter((grid) => grid.breakpoint);
  const gridsWithoutBreakpoint = grids.filter((grid) => !grid.breakpoint);

  // Process grids with breakpoints through the normal flow
  const gridByBreakpoints = getGridByBreakpoints(gridsWithBreakpoint);

  const filteredGridByBreakpoints = Object.entries(gridByBreakpoints).reduce(
    (acc, [breakpoint, grids]) => {
      let resultGrids = [...grids];
      const hasOptions = grids.some((grid) => Boolean(grid.groupId));

      if (hasOptions) {
        const groupId = getGroupId(breakpoint as KnownBreakpoints, grids, optionIds) as string;
        resultGrids = grids.filter((grid) => grid.groupId === groupId);

        if (!resultGrids.length) {
          const firstGroupId = grids.find((grid) => Boolean(grid.groupId))?.groupId;
          resultGrids = grids.filter((grid) => grid.groupId === firstGroupId);

          logger.error(
            `Unsupported grid option ID - ${groupId}, defaulting to grid option ${firstGroupId}.`,
            {
              breakpoint,
            },
          );
        }
      }

      return {
        ...acc,
        [breakpoint]: resultGrids,
      };
    },
    {} as Record<KnownBreakpoints, GridInterface[]>,
  );

  // Combine the filtered grids with breakpoints and the grids without breakpoints
  return [...Object.values(filteredGridByBreakpoints).flat(), ...gridsWithoutBreakpoint];
};
